import { injectable } from 'inversify';
import type {
  IPanelPagoRepository,
  PeriodoPago,
  PanelPagoEstado,
  PanelPagoFormData,
  PanelPagoFilters,
} from '@/domain/repositories/IPanelPagoRepository';
import { PanelPago } from '@/domain/entities/PanelPago';

@injectable()
export class MockPanelPagoRepository implements IPanelPagoRepository {
  private mockPagos: PanelPago[] = [
    PanelPago.create(1, 1, 'Clínica San José', new Date('2026-01-15'), '2026-01', 5000, '1', 1, 1, 'Abonado', 0, 'Transferencia', undefined, undefined),
    PanelPago.create(2, 2, 'Clínica Central', new Date('2025-12-20'), '2025-12', 12000, '2', 1, 2, 'Mora', 5000, 'Efectivo', undefined, undefined),
    PanelPago.create(3, 3, 'Clínica Norte', null, '2025-11', 5000, '1', 3, 3, 'Impago', 10000, undefined, undefined, undefined),
  ];

  private mockEstados: PanelPagoEstado[] = [
    { id: 1, estado: 'Abonado' },
    { id: 2, estado: 'Mora' },
    { id: 3, estado: 'Impago' },
    { id: 4, estado: 'Gratis' },
  ];

  async getPeriodoPagoByTenantId(tenantId: number | string): Promise<PeriodoPago | null> {
    await new Promise(resolve => setTimeout(resolve, 200));
    if (typeof tenantId !== 'number') return null; // Mock solo tiene clinicaId numérico
    const pago = this.mockPagos.find(p => p.clinicaId.getValue() === tenantId);
    if (pago) {
      return {
        fechaPeriodoPago: pago.fechaPeriodoPago,
        estadoPagoId: pago.estadoPagoId,
        estadoPagoDescripcion: pago.estadoPagoDescripcion,
      };
    }
    return null;
  }

  async getAll(filters?: PanelPagoFilters): Promise<PanelPago[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    let pagos = [...this.mockPagos];
    if (filters?.anio || filters?.mes) {
      pagos = pagos.filter(p => {
        const [anioStr, mesStr] = p.fechaPeriodoPago.split('-');
        const anio = Number(anioStr);
        const mes = Number(mesStr);
        if (filters?.anio && anio !== filters.anio) return false;
        if (filters?.mes && mes !== filters.mes) return false;
        return true;
      });
    }
    if (filters?.estadoPagoId) {
      pagos = pagos.filter(p => p.estadoPagoId === filters.estadoPagoId);
    }
    return pagos;
  }

  async getAllEstadosPago(): Promise<PanelPagoEstado[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return [...this.mockEstados];
  }

  async getByTenantId(tenantId: number | string): Promise<PanelPago[]> {
    await new Promise(resolve => setTimeout(resolve, 200));
    if (typeof tenantId !== 'number') return [];
    return this.mockPagos.filter(p => p.clinicaId.getValue() === tenantId);
  }

  async create(data: PanelPagoFormData): Promise<PanelPago> {
    await new Promise(resolve => setTimeout(resolve, 400));
    const newId = Math.max(...this.mockPagos.map(p => p.id), 0) + 1;
    // Por defecto, si hay fecha de pago, está abonado, sino está en mora
    const estadoId = data.estadoPagoId ?? (data.fechaPago ? 1 : 2);
    const estado = this.mockEstados.find(e => e.id === estadoId) || this.mockEstados[0];
    // Mock usa clinicaId numérico; con tenantId (GUID) usamos 0 como placeholder
    const clinicaIdForMock = data.clinicaId ?? 0;
    
    const newPago = PanelPago.create(
      newId,
      clinicaIdForMock,
      'Clínica', // nombreClinica - debería venir del tenant
      data.fechaPago,
      data.fechaPeriodoPago,
      data.monto,
      data.planId,
      data.medioPago,
      estado.id,
      estado.estado,
      0, // deudaAcumulada
      undefined, // medioPagoDescripcion
      undefined, // usuarioRegistroId
      data.nombreArchivoComprobante,
      data.notas
    );

    this.mockPagos.push(newPago);
    return newPago;
  }

  async updateOrCreate(data: PanelPagoFormData): Promise<PanelPago> {
    await new Promise(resolve => setTimeout(resolve, 400));
    const matchId = data.clinicaId ?? (data.tenantId ? 0 : undefined);
    const existing = matchId !== undefined
      ? this.mockPagos.find(p => p.clinicaId.getValue() === matchId && p.fechaPeriodoPago === data.fechaPeriodoPago)
      : undefined;

    if (existing) {
      // Actualizar existente
      const estadoId = data.estadoPagoId ?? (data.fechaPago ? 1 : existing.estadoPagoId);
      const estado = this.mockEstados.find(e => e.id === estadoId) || this.mockEstados.find(e => e.id === existing.estadoPagoId)!;
      
      const clinicaIdForMock = data.clinicaId ?? 0;
      const updated = PanelPago.create(
        existing.id,
        clinicaIdForMock,
        existing.nombreClinica,
        data.fechaPago,
        data.fechaPeriodoPago,
        data.monto,
        data.planId,
        data.medioPago,
        estado.id,
        estado.estado,
        existing.deudaAcumulada,
        undefined, // medioPagoDescripcion
        undefined, // usuarioRegistroId
        data.nombreArchivoComprobante,
        data.notas
      );
      
      const index = this.mockPagos.findIndex(p => p.id === existing.id);
      if (index !== -1) {
        this.mockPagos[index] = updated;
      }
      
      return updated;
    }

    return this.create(data);
  }

  async getByPeriodo(periodo: string): Promise<PanelPago[]> {
    await new Promise(resolve => setTimeout(resolve, 200));
    return this.mockPagos.filter(p => p.fechaPeriodoPago === periodo);
  }

  async cancel(id: number): Promise<PanelPago> {
    await new Promise(resolve => setTimeout(resolve, 200));
    const existing = this.mockPagos.find(p => p.id === id);
    if (!existing) {
      throw new Error('Pago no encontrado');
    }

    const estado = this.mockEstados.find(e => e.estado === 'Impago') || this.mockEstados[2];
    const updated = PanelPago.create(
      existing.id,
      existing.clinicaId.getValue(),
      existing.nombreClinica,
      null,
      existing.fechaPeriodoPago,
      existing.monto,
      existing.planId,
      null,
      estado.id,
      estado.estado,
      existing.deudaAcumulada,
      undefined,
      existing.usuarioRegistroId || undefined,
      null,
      existing.notas || undefined
    );

    const index = this.mockPagos.findIndex(p => p.id === id);
    if (index !== -1) {
      this.mockPagos[index] = updated;
    }
    return updated;
  }

  async getReciboHtml(id: number): Promise<string> {
    const pago = this.mockPagos.find((p) => p.id === id);
    if (!pago) throw new Error('Pago no encontrado');
    return `<!DOCTYPE html><html><body><h1>Comprobante #${id}</h1><p>${pago.nombreClinica} — $${pago.monto}</p><button onclick="window.print()">Imprimir</button></body></html>`;
  }
}
